import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { defaultUserSettings, UserSettings, validateUserSettings } from '../models/user-settings.model';
import { ProfileService } from './profile.service';
import { NotificationService } from '../../core/services/notification.service';

const LOCAL_STORAGE_KEY = 'je-userSettings';

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
    private settingsSubject = new BehaviorSubject<UserSettings>(this.loadFromLocalStorage());
    public settings$ = this.settingsSubject.asObservable();
    private userId: number | null = null;

    constructor(
        private apiService: ProfileService,
        private notificationService: NotificationService
    ) {}

    public initialize(userId: number) {
        this.userId = userId;
        this.loadFromBackend(userId);
    }

    public updateSettings(settings: Partial<UserSettings>): void {
        const updated = { ...this.settingsSubject.value, ...settings };

        if (this.userId != null) {
            const json = JSON.stringify(updated);
            this.apiService.setUserSettings(this.userId, json).subscribe({
                next: async () => {
                    await this.notificationService.successTranslate('COMMON.SAVE_SUCCESSFULLY');
                },
            });
        }

        this.saveToLocalStorage(updated);
        this.settingsSubject.next(updated);
    }

    public updateSettingsWithoutErrorHandling(settings: Partial<UserSettings>): Observable<boolean> {
        const updated = { ...this.settingsSubject.value, ...settings };

        const json = JSON.stringify(updated);
        this.saveToLocalStorage(updated);
        this.settingsSubject.next(updated);
        return this.apiService.setUserSettings(this.userId, json);
    }

    public loadFromBackend(userId: number) {
        this.apiService.getUserSettings(userId)?.subscribe(json => {
            try {
                const parsed = JSON.parse(json);
                const validated = validateUserSettings(parsed);
                this.settingsSubject.next(validated);
                this.saveToLocalStorage(validated);
            } catch {
                console.warn('Failed to parse or validate settings from backend');
            }
        });
    }

    private loadFromLocalStorage(): UserSettings {
        const json = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!json) return defaultUserSettings;
        try {
            const parsed = JSON.parse(json);
            return validateUserSettings(parsed);
        } catch {
            return defaultUserSettings;
        }
    }

    private saveToLocalStorage(settings: UserSettings) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
    }
}
